import React, { useState, useEffect } from "react";
import { getUserDetails, updateGraphPreferences } from "../api/user";
import {
    Typography, Box, Card, CardContent, Switch, TextField, Button,
    Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
    Grid, Divider, IconButton, Tooltip, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useDarkMode } from '../components/DarkModeContext';
import colors from "../colour";
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SecurityIcon from '@mui/icons-material/Security';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import {
    getSmartMeters,
    getTimeWindows,
    createTimeWindow,
    updateTimeWindow,
    deleteTimeWindow,
    getAlarmEvents,
    getAlarmEmailConfig,
    createAlarmEmailConfig,
    updateAlarmEmailConfig,
} from "../api/others.jsx";

// ─── Shared sub-components ───────────────────────────────────────────────────

function InfoRow({ icon, label, value }) {
    return (
        <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5,
            p: 1.5, borderRadius: 3,
            "&:hover": { bgcolor: "action.hover" },
            transition: "background 0.2s"
        }}>
            <Box sx={{ color: "primary.main", mt: "2px" }}>{icon}</Box>
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
            </Box>
        </Box>
    );
}

function SettingsToggleRow({ label, description, checked, onChange }) {
    return (
        <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            p: 2, borderRadius: 3, bgcolor: "action.hover",
            "&:hover": { bgcolor: "action.selected" }, transition: "background 0.2s"
        }}>
            <Box>
                <Typography sx={{ fontWeight: 500 }}>{label}</Typography>
                {description && (
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                )}
            </Box>
            <Switch checked={checked} onChange={onChange} />
        </Box>
    );
}

function MetricCard({ label, value, bgcolor }) {
    return (
        <Box sx={{ p: 3, borderRadius: 3, bgcolor, color: "white" }}>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>{label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>{value}</Typography>
        </Box>
    );
}

// ─── Alarm: Level chip ───────────────────────────────────────────────────────

function LevelChip({ levelName }) {
    const colorMap = {
        critical: { bgcolor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
        warning: { bgcolor: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
        ok: { bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
    };
    const style = colorMap[levelName] || { bgcolor: "grey.100", color: "text.secondary" };
    return (
        <Box component="span" sx={{
            fontSize: "0.7rem", fontWeight: 600, px: 1.5, py: 0.25,
            borderRadius: "999px", ...style
        }}>
            {levelName}
        </Box>
    );
}

// ─── Alarm: Inline TimeWindow Modal ─────────────────────────────────────────
// Replaces the external TimeWindowModal component entirely.

const LEVEL_DEFAULTS = [
    { level_name: "ok", priority: 1, operator: "<", value_low: "", value_high: "" },
    { level_name: "warning", priority: 2, operator: "between", value_low: "", value_high: "" },
    { level_name: "critical", priority: 3, operator: ">", value_low: "", value_high: "" },
];

const OPERATORS = [
    { value: "<", label: "Less than" },
    { value: "<=", label: "Less than or equal" },
    { value: ">", label: "Greater than" },
    { value: ">=", label: "Greater than or equal" },
    { value: "==", label: "Equal to" },
    { value: "between", label: "Between" },
    { value: "outside", label: "Outside" },
];

function TimeWindowModal({ serial_number, clientId, window: editWindow, onClose, onSaved }) {
    const isEdit = !!editWindow;

    const [label, setLabel] = useState(editWindow?.label || "");
    const [startTime, setStartTime] = useState(editWindow?.start_time || "00:00");
    const [endTime, setEndTime] = useState(editWindow?.end_time || "23:59");
    const [active, setActive] = useState(editWindow?.active ?? true);
    const [levels, setLevels] = useState(
        editWindow?.levels?.length
            ? editWindow.levels.map(l => ({ ...l }))
            : LEVEL_DEFAULTS.map(l => ({ ...l }))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const updateLevel = (idx, field, value) => {
        setLevels(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    };

    const needsHighValue = (op) => op === "between" || op === "outside";

    const handleSubmit = async () => {
        if (!startTime || !endTime) { setError("Start and end time are required."); return; }
        setSaving(true);
        setError("");
        try {
            const payload = {
                serial_number,
                label,
                start_time: startTime,
                end_time: endTime,
                active,
                levels: levels.map(l => ({
                    level_name: l.level_name,
                    priority: l.priority,
                    operator: l.operator,
                    value_low: parseFloat(l.value_low) || 0,
                    value_high: parseFloat(l.value_high) || 0,
                })),
            };
            if (isEdit) {
                await updateTimeWindow(editWindow.id, { ...payload, client_id: clientId });
            } else {
                await createTimeWindow(payload, clientId);
            }
            onSaved();
        } catch (e) {
            console.error(e);
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 4 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {isEdit ? "Edit alarm window" : "New alarm window"}
                </Typography>
                <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {/* Label */}
                <TextField
                    label="Label (optional)"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    fullWidth size="small"
                    sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                {/* Time range */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                    <TextField
                        label="Start time"
                        type="time"
                        value={startTime}
                        onChange={e => setStartTime(e.target.value)}
                        size="small" InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <TextField
                        label="End time"
                        type="time"
                        value={endTime}
                        onChange={e => setEndTime(e.target.value)}
                        size="small" InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                </Box>

                {/* Active toggle */}
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 2, py: 1, mb: 2.5, borderRadius: 2, bgcolor: "action.hover"
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Active</Typography>
                    <Switch size="small" checked={active} onChange={e => setActive(e.target.checked)} />
                </Box>

                {/* Alarm levels */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Alarm levels (kW)
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {levels.map((lvl, idx) => {
                        const levelColor =
                            lvl.level_name === "critical" ? { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c" } :
                                lvl.level_name === "warning" ? { bg: "#fffbeb", border: "#fde68a", text: "#b45309" } :
                                    { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" };
                        return (
                            <Box key={lvl.level_name} sx={{
                                border: `1px solid ${levelColor.border}`,
                                bgcolor: levelColor.bg,
                                borderRadius: 3, p: 2
                            }}>
                                <Typography sx={{
                                    fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: "0.06em", color: levelColor.text, mb: 1.5
                                }}>
                                    {lvl.level_name}
                                </Typography>
                                <Box sx={{ display: "grid", gridTemplateColumns: needsHighValue(lvl.operator) ? "1.5fr 1fr 1fr" : "1.5fr 1fr", gap: 1.5 }}>
                                    <FormControl size="small">
                                        <InputLabel>Condition</InputLabel>
                                        <Select
                                            value={lvl.operator}
                                            onChange={e => updateLevel(idx, "operator", e.target.value)}
                                            label="Condition"
                                            sx={{ borderRadius: 2, bgcolor: "white" }}
                                        >
                                            {OPERATORS.map(op => (
                                                <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label={needsHighValue(lvl.operator) ? "Low (kW)" : "Value (kW)"}
                                        size="small" type="number"
                                        value={lvl.value_low}
                                        onChange={e => updateLevel(idx, "value_low", e.target.value)}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "white" } }}
                                    />
                                    {needsHighValue(lvl.operator) && (
                                        <TextField
                                            label="High (kW)"
                                            size="small" type="number"
                                            value={lvl.value_high}
                                            onChange={e => updateLevel(idx, "value_high", e.target.value)}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "white" } }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined"
                    sx={{ borderRadius: 3, textTransform: "none" }}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}
                    sx={{ borderRadius: 3, textTransform: "none", bgcolor: "grey.900", "&:hover": { bgcolor: "grey.700" } }}>
                    {saving ? "Saving..." : isEdit ? "Save changes" : "Create window"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Alarm: Meter row ────────────────────────────────────────────────────────

function MeterRow({ meter }) {
    const [windows, setWindows] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editWindow, setEditWindow] = useState(null);
    const [lastAlarm, setLastAlarm] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getAlarmEvents({ serial_number: meter.serial_number })
            .then((result) => { if (result?.data?.length) setLastAlarm(result.data[0]); })
            .catch(() => { });
    }, [meter.serial_number]);

    const loadWindows = async () => {
        setLoading(true);
        try {
            const result = await getTimeWindows({ serial_number: meter.serial_number, client_id: meter.client_id });
            if (!result) return;
            setWindows(result.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        loadWindows(); // 👈 load on mount
    }, [meter.serial_number]);

    const handleToggle = () => {
        setExpanded(v => !v);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this alarm window?")) return;
        await deleteTimeWindow(id);
        loadWindows();
    };

    const handleSaved = () => {
        setShowModal(false);
        setEditWindow(null);
        loadWindows();
    };

    return (
        <>
            <Box sx={{
                borderRadius: 3, border: "1px solid", borderColor: "divider",
                overflow: "hidden", transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }
            }}>
                {/* Summary row */}
                <Box sx={{
                    display: "grid", gridTemplateColumns: "1fr auto auto auto",
                    alignItems: "center", gap: 2, px: 2.5, py: 2, bgcolor: "background.paper",
                }}>
                    <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "text.secondary", fontWeight: 600 }}>
                        {meter.serial_number}
                    </Typography>

                    {windows.length > 0
                        ? <Chip label={`${windows.length} window${windows.length > 1 ? "s" : ""}`} size="small"
                            sx={{ bgcolor: "#f0fdf4", color: "#15803d", fontWeight: 600, fontSize: "0.7rem" }} />
                        : <Chip label="No alarms" size="small"
                            sx={{ bgcolor: "action.hover", color: "text.disabled", fontSize: "0.7rem" }} />
                    }

                    {lastAlarm
                        ? <LevelChip levelName={lastAlarm.level_name} />
                        : <Typography sx={{ color: "text.disabled", fontSize: "0.75rem" }}>—</Typography>
                    }

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="outlined"
                            startIcon={<AddAlertIcon sx={{ fontSize: "0.9rem !important" }} />}
                            onClick={() => { setEditWindow(null); setShowModal(true); }}
                            sx={{ fontSize: "0.75rem", textTransform: "none", borderRadius: 2, py: 0.5 }}>
                            Add alarm
                        </Button>
                        <IconButton size="small" onClick={handleToggle}
                            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, px: 0.75 }}>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Expanded windows */}
                {expanded && (
                    <Box sx={{ bgcolor: "action.hover", borderTop: "1px solid", borderColor: "divider", px: 2.5, py: 2 }}>
                        {loading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="body2" color="text.secondary">Loading...</Typography>
                            </Box>
                        ) : windows.length === 0 ? (
                            <Typography variant="body2" color="text.disabled">
                                No alarm windows yet. Click "Add alarm" to create one.
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {windows.map((win) => (
                                    <Box key={win.id} sx={{
                                        bgcolor: "background.paper", border: "1px solid",
                                        borderColor: "divider", borderRadius: 3, px: 2.5, py: 2
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", flex: 1 }}>
                                                {win.label || "Unnamed window"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {win.start_time} – {win.end_time}
                                            </Typography>
                                            <Chip label={win.active ? "Active" : "Inactive"} size="small"
                                                sx={win.active
                                                    ? { bgcolor: "#f0fdf4", color: "#15803d", fontSize: "0.7rem", fontWeight: 600 }
                                                    : { bgcolor: "action.hover", color: "text.disabled", fontSize: "0.7rem" }
                                                }
                                            />
                                            <Button size="small" variant="outlined"
                                                onClick={() => { setEditWindow(win); setShowModal(true); }}
                                                sx={{ fontSize: "0.72rem", textTransform: "none", borderRadius: 2, py: 0.4, minWidth: 0 }}>
                                                Edit
                                            </Button>
                                            <IconButton size="small" color="error"
                                                onClick={() => handleDelete(win.id)}
                                                sx={{ border: "1px solid", borderColor: "error.light", borderRadius: 2, p: 0.5 }}>
                                                <DeleteOutlineIcon sx={{ fontSize: "0.95rem" }} />
                                            </IconButton>
                                        </Box>

                                        {win.levels?.length > 0 && (
                                            <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                                                {win.levels.sort((a, b) => a.priority - b.priority).map((l) => (
                                                    <Box key={l.level_name} sx={{
                                                        borderRadius: 2, px: 1.5, py: 1,
                                                        ...(l.level_name === "critical" ? { bgcolor: "#fef2f2", color: "#b91c1c" } :
                                                            l.level_name === "warning" ? { bgcolor: "#fffbeb", color: "#b45309" } :
                                                                { bgcolor: "#f0fdf4", color: "#15803d" })
                                                    }}>
                                                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                                            {l.level_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.75, mt: 0.25 }}>
                                                            {l.operator === "between" || l.operator === "outside"
                                                                ? `${l.operator} ${l.value_low}–${l.value_high} kW`
                                                                : `${l.operator} ${l.value_low} kW`
                                                            }
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Inline modal — no external component needed */}
            {showModal && (
                <TimeWindowModal
                    serial_number={meter.serial_number}
                    clientId={meter.client_id}
                    window={editWindow}
                    onClose={() => { setShowModal(false); setEditWindow(null); }}
                    onSaved={handleSaved}
                />
            )}
        </>
    );
}

// ─── Alarm: Management section ───────────────────────────────────────────────

function AlarmManagementSection({ clientId, cardSx }) {
    const [meters, setMeters] = useState([]);
    const [metersLoading, setMetersLoading] = useState(true);
    const [metersError, setMetersError] = useState(null);
    const [search, setSearch] = useState("");

    const [emailConfigId, setEmailConfigId] = useState(null);
    const [warningLimit, setWarningLimit] = useState(10);
    const [criticalLimit, setCriticalLimit] = useState(10);
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailSaved, setEmailSaved] = useState(false);

    // Max emails/day allowed per limit type = number of meters * 2
    const maxAllowedLimit = meters.length * 2;

    useEffect(() => {
        if (!clientId) return;
        getSmartMeters(clientId)
            .then(({ data }) => setMeters(data))
            .catch(() => setMetersError("Failed to load meters."))
            .finally(() => setMetersLoading(false));

        getAlarmEmailConfig(clientId)
            .then(({ data }) => {
                if (data.length > 0) {
                    setEmailConfigId(data[0].id);
                    setWarningLimit(data[0].warning_limit);
                    setCriticalLimit(data[0].critical_limit);
                }
            })
            .catch(() => { });
    }, [clientId]);

    // Clamp a typed value between 1 and maxAllowedLimit
    const clampLimit = (value) => {
        if (value === "") return "";
        let num = Number(value);
        if (Number.isNaN(num)) return "";
        if (num < 1) num = 1;
        if (maxAllowedLimit > 0 && num > maxAllowedLimit) num = maxAllowedLimit;
        return num;
    };

    const handleWarningChange = (e) => {
        setWarningLimit(clampLimit(e.target.value));
    };

    const handleCriticalChange = (e) => {
        setCriticalLimit(clampLimit(e.target.value));
    };

    const isWarningInvalid = maxAllowedLimit > 0 && Number(warningLimit) > maxAllowedLimit;
    const isCriticalInvalid = maxAllowedLimit > 0 && Number(criticalLimit) > maxAllowedLimit;
    const saveDisabled = emailSaving || isWarningInvalid || isCriticalInvalid || warningLimit === "" || criticalLimit === "";

    const handleEmailSave = async () => {
        // Final safety clamp before hitting the API, in case state got out of sync
        const safeWarning = Math.min(Number(warningLimit) || 1, maxAllowedLimit || Number(warningLimit) || 1);
        const safeCritical = Math.min(Number(criticalLimit) || 1, maxAllowedLimit || Number(criticalLimit) || 1);

        setEmailSaving(true);
        setEmailSaved(false);
        try {
            const payload = { client_id: clientId, warning_limit: safeWarning, critical_limit: safeCritical };
            if (emailConfigId) {
                await updateAlarmEmailConfig(emailConfigId, payload);
            } else {
                const { data } = await createAlarmEmailConfig(payload);
                setEmailConfigId(data.id);
            }
            setWarningLimit(safeWarning);
            setCriticalLimit(safeCritical);
            setEmailSaved(true);
            setTimeout(() => setEmailSaved(false), 2000);
        } catch (e) { console.error(e); }
        finally { setEmailSaving(false); }
    };

    const filtered = meters.filter(m =>
        m.serial_number.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Grid item xs={12}>
            <Card sx={cardSx}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <NotificationsPausedIcon color="primary" /> Alarm Management
                        </Typography>
                        <TextField
                            size="small" placeholder="Search serial number..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 3, fontSize: "0.8rem" } }}
                        />
                    </Box>
                    <Divider sx={{ mb: 2.5 }} />

                    {!metersLoading && !metersError && (
                        <Box sx={{
                            display: "grid", gridTemplateColumns: "1fr auto auto auto",
                            gap: 2, px: 2.5, py: 1, mb: 1,
                            bgcolor: "action.hover", borderRadius: 2,
                        }}>
                            {["Serial Number", "Alarm Windows", "Last Alarm", ""].map((h, i) => (
                                <Typography key={i} variant="caption" color="text.disabled"
                                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {metersLoading && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">Loading meters...</Typography>
                        </Box>
                    )}
                    {metersError && <Alert severity="error" sx={{ borderRadius: 3 }}>{metersError}</Alert>}

                    {!metersLoading && !metersError && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {filtered.length === 0
                                ? <Typography variant="body2" color="text.disabled" sx={{ py: 2, px: 1 }}>No meters found.</Typography>
                                : filtered.map(meter => <MeterRow key={meter.serial_number} meter={meter} />)
                            }
                        </Box>
                    )}

                    {!metersLoading && !metersError && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Email alert limits</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Max alarm emails sent per meter per day for this client. Applies to all meters.

                                <Typography component="span" variant="body2" sx={{ color: "error.main", fontWeight: 700 }}>
                                    {" "}Limit is capped at{" "} {maxAllowedLimit}
                                </Typography>.
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Warning emails / day" type="number" size="small" fullWidth
                                        inputProps={{ min: 1, max: maxAllowedLimit }} value={warningLimit}
                                        onChange={handleWarningChange}
                                        error={isWarningInvalid}
                                        helperText={isWarningInvalid ? `Max allowed is ${maxAllowedLimit}` : " "}
                                        sx={{
                                            "& .MuiOutlinedInput-root": { borderRadius: 3 },
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fde68a" },
                                            "& .MuiInputBase-input": { bgcolor: "#fffbeb", borderRadius: 3 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Critical emails / day" type="number" size="small" fullWidth
                                        inputProps={{ min: 1, max: maxAllowedLimit }} value={criticalLimit}
                                        onChange={handleCriticalChange}
                                        error={isCriticalInvalid}
                                        helperText={isCriticalInvalid ? `Max allowed is ${maxAllowedLimit}` : " "}
                                        sx={{
                                            "& .MuiOutlinedInput-root": { borderRadius: 3 },
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fecaca" },
                                            "& .MuiInputBase-input": { bgcolor: "#fef2f2", borderRadius: 3 }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Button variant="contained" onClick={handleEmailSave} disabled={saveDisabled}
                                    sx={{ borderRadius: 3, textTransform: "none", bgcolor: "grey.900", "&:hover": { bgcolor: "grey.700" } }}>
                                    {emailSaving ? "Saving..." : "Save limits"}
                                </Button>
                                {emailSaved && (
                                    <Typography variant="body2" sx={{ color: "success.main", fontWeight: 500 }}>✓ Saved</Typography>
                                )}
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const UserInfo = () => {
    const [userDetail, setUserDetail] = useState(null);
    const { darkMode, toggleDarkMode } = useDarkMode();
    const [loading, setLoading] = useState(true);

    const [emailNotifications, setEmailNotifications] = useState(true);
    const [pushNotifications, setPushNotifications] = useState(true);
    const [weatherAlerts, setWeatherAlerts] = useState(true);

    const [sessionTimeout, setSessionTimeout] = useState(true);
    const [sessionDuration, setSessionDuration] = useState("30");
    const [twoFactorAuth, setTwoFactorAuth] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [companyAddress, setCompanyAddress] = useState("");

    const [graphToggles, setGraphToggles] = useState({});
    const GRAPH_LABELS = {
        consumption: "Consumption (kW)",
        phase_diff_kw: "Phase Difference (kW)",
        phase_diff_voltage: "Phase Difference (V)",
        phase_diff_current: "Phase Difference (A)",
        power_factor: "Power Factor",
        apparent_power: "Apparent Power",
        voltage_harmonics: "Voltage Harmonics (%)",
        current_harmonics: "Current Harmonics (%)",
        building_load: "Building Load Percentage",
    };
    // Load on page open
    useEffect(() => {
        const fetchUserInfo = async () => {
            const data = await getUserDetails();
            setGraphToggles(data.graph_prefs);
        };
        fetchUserInfo();
    }, []);

    // Save on toggle
    const handleGraphToggle = async (name) => {
        if (name === "consumption") return;  // ← extra safety, can't toggle

        const updated = { ...graphToggles, [name]: !graphToggles[name] };
        setGraphToggles(updated);
        await updateGraphPreferences({ [name]: !graphToggles[name] });
    };

    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API,
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await getUserDetails();
                setUserDetail(data);
                setDisplayName(data.company_name || "");
                setEmail(data.email || "");
                setPhoneNumber(data.phone_number || "");
                setCompanyAddress(data.company_address || "");
            } catch (error) {
                console.error("Error fetching user data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const center = userDetail?.latitude && userDetail?.longitude
        ? { lat: parseFloat(userDetail.latitude), lng: parseFloat(userDetail.longitude) }
        : { lat: 3.0256, lng: 101.6214 };

    const handleSave = () => {
        console.log("Saving:", { displayName, email, phoneNumber, companyAddress });
        setIsEditing(false);
    };



    if (loading) {
        return (
            <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!userDetail) {
        return (
            <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                <Alert severity="error">Failed to load user data</Alert>
                <Button onClick={() => window.location.reload()} sx={{ mt: 2 }} variant="contained">Retry</Button>
            </Box>
        );
    }

    const val = (v) => (v !== null && v !== undefined && v !== "" ? v : "N/A");

    const cardSx = {
        borderRadius: 4,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        bgcolor: darkMode ? colors.d2 : "white",
    };

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: darkMode ? colors.d1 : colors.l1, overflow: "auto" }}>

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <Box sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                p: 2, borderBottom: `1px solid ${darkMode ? colors.d3 : colors.l3}`,
                bgcolor: darkMode ? colors.d1 : colors.l1,
                position: "sticky", top: 0, zIndex: 10
            }}>
                <Typography variant="h4" sx={{
                    fontFamily: "Montserrat", fontWeight: "bold",
                    fontSize: { xs: "22px", md: "36px" },
                    color: darkMode ? colors.f2 : colors.f3, ml: "2rem"
                }}>
                    User Information
                </Typography>
                {/* <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                    <IconButton onClick={toggleDarkMode} sx={{ mr: 2 }}>
                        {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>
                </Tooltip> */}
            </Box>

            <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>

                {/* ── Profile Header ──────────────────────────────────────────── */}
                <Card sx={{
                    mb: 4, borderRadius: 4, border: 0,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white"
                }}>
                    <CardContent sx={{ display: "flex", alignItems: "center", gap: 3, p: 4 }}>
                        <Box sx={{
                            width: 96, height: 96, borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.2)",
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <BusinessIcon sx={{ fontSize: 50 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                {userDetail.company_name}
                            </Typography>
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.85 }}>
                                <EmailIcon sx={{ fontSize: 16 }} /> {userDetail.email}
                            </Typography>
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, opacity: 0.85 }}>
                                <LocalPhoneIcon sx={{ fontSize: 16 }} /> {userDetail.phone_number}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* ── Contact Info + System Specs ─────────────────────────────── */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <BusinessIcon color="primary" /> Contact Information
                                    </Typography>
                                    <Tooltip title={isEditing ? "Save" : "Edit"}>
                                        <IconButton size="small" onClick={isEditing ? handleSave : () => setIsEditing(true)}>
                                            {isEditing ? <SaveIcon /> : <EditIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                {isEditing ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <TextField label="Company Name" value={displayName} onChange={e => setDisplayName(e.target.value)} fullWidth size="small" />
                                        <TextField label="Company Address" value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} fullWidth multiline rows={2} size="small" />
                                        <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} fullWidth size="small" />
                                        <TextField label="Phone Number" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} fullWidth size="small" />
                                        <Button variant="contained" onClick={handleSave} sx={{ mt: 1, borderRadius: 3 }}>Save Changes</Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        <InfoRow icon={<BusinessIcon sx={{ fontSize: 20 }} />} label="Company Address" value={val(userDetail.company_address)} />
                                        <InfoRow icon={<EmailIcon sx={{ fontSize: 20 }} />} label="Email" value={val(userDetail.email)} />
                                        <InfoRow icon={<LocalPhoneIcon sx={{ fontSize: 20 }} />} label="Phone" value={val(userDetail.phone_number)} />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <WbSunnyIcon color="primary" /> System Specifications
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                    {[
                                        { icon: <WbSunnyIcon />, label: "Solar System Size", value: userDetail.solar_system_size ? `${userDetail.solar_system_size} kWp` : "N/A" },
                                        { icon: <FlashOnIcon />, label: "Solar Inverter Size", value: userDetail.solar_inverter_size ? `${userDetail.solar_inverter_size} kWac` : "N/A" },
                                        { icon: <BatteryFullIcon />, label: "Battery Capacity", value: userDetail.battery_capacity_size ? `${userDetail.battery_capacity_size} kWh` : "N/A" },
                                    ].map(({ icon, label, value }) => (
                                        <Box key={label}>
                                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                                {React.cloneElement(icon, { color: "primary" })} {label}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>{value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Appearance + Notifications ──────────────────────────────── */}
                {/* <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <WbSunnyIcon color="primary" /> Appearance
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <SettingsToggleRow
                                    label="Dark Mode" description="Toggle between light and dark theme"
                                    checked={darkMode} onChange={toggleDarkMode}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <NotificationsActiveIcon color="primary" /> Notifications
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    <SettingsToggleRow label="Email Notifications" description="Receive updates via email" checked={emailNotifications} onChange={e => setEmailNotifications(e.target.checked)} />
                                    <SettingsToggleRow label="Push Notifications" description="Get instant alerts" checked={pushNotifications} onChange={e => setPushNotifications(e.target.checked)} />
                                    <SettingsToggleRow label="Weather Alerts" description="Notifications for weather changes" checked={weatherAlerts} onChange={e => setWeatherAlerts(e.target.checked)} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid> */}

                {/* ── Alarm Management ────────────────────────────────────────── */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <AlarmManagementSection clientId={userDetail?.client_id} cardSx={cardSx} />
                </Grid>

                {/* ── Account Settings + Security ─────────────────────────────── */}
                {/* <Grid container spacing={3} sx={{ mb: 4, alignItems: "stretch" }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ ...cardSx, height: "100%" }}>
                            <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <BusinessIcon color="primary" /> Account Settings
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1, justifyContent: "space-between" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <TextField label="Display Name" defaultValue={userDetail.company_name} fullWidth size="small" />
                                        <TextField label="Email Address" type="email" defaultValue={userDetail.email} fullWidth size="small" />
                                        <TextField label="Phone Number" type="tel" defaultValue={userDetail.phone_number} fullWidth size="small" />
                                    </Box>
                                    <Button variant="outlined" fullWidth sx={{ borderRadius: 3, mt: 1 }}>Change Password</Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ ...cardSx, height: "100%" }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <SecurityIcon color="primary" /> Security &amp; Privacy
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    <SettingsToggleRow label="Two-Factor Authentication" description="Add extra layer of security" checked={twoFactorAuth} onChange={e => setTwoFactorAuth(e.target.checked)} />
                                    <SettingsToggleRow label="Session Timeout" description="Auto-logout after inactivity" checked={sessionTimeout} onChange={e => setSessionTimeout(e.target.checked)} />
                                    {sessionTimeout && (
                                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                            <InputLabel>Session Duration</InputLabel>
                                            <Select value={sessionDuration} onChange={e => setSessionDuration(e.target.value)} label="Session Duration" sx={{ borderRadius: 3 }}>
                                                <MenuItem value="15">15 minutes</MenuItem>
                                                <MenuItem value="30">30 minutes</MenuItem>
                                                <MenuItem value="60">1 hour</MenuItem>
                                                <MenuItem value="120">2 hours</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                    <Button variant="outlined" color="error" fullWidth sx={{ borderRadius: 3, mt: 1 }}>
                                        View Login History
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid> */}

                {/* ── Graph Menu ──────────────────────────────────────────────── */}
                <Card sx={{ ...cardSx, mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <BarChartIcon color="primary" /> Graph Menu
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            {Object.entries(graphToggles).map(([name, checked]) => (
                                <Grid item xs={12} sm={6} md={4} key={name}>
                                    <Box sx={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        p: 2, borderRadius: 3, bgcolor: "action.hover",
                                        "&:hover": { bgcolor: "action.selected" }, transition: "background 0.2s"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography sx={{ fontWeight: 500 }}>
                                                {GRAPH_LABELS[name] || name}
                                            </Typography>
                                            {/* Show lock icon if it's locked */}
                                            {name === "consumption" && (
                                                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                                    (Always On)
                                                </Typography>
                                            )}
                                        </Box>
                                        <Switch
                                            checked={checked}
                                            onChange={() => handleGraphToggle(name)}
                                            disabled={name === "consumption"}  // ← locked
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Performance Metrics ─────────────────────────────────────── */}
                <Card sx={{ ...cardSx, mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <TrendingUpIcon color="primary" /> Performance Metrics
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            {[
                                { label: "P90 (Conservative)", value: userDetail.p90_estimate ? `${userDetail.p90_estimate} kWh/year` : "N/A", bg: "primary.main" },
                                { label: "P50 (Expected)", value: userDetail.p50_estimate ? `${userDetail.p50_estimate} kWh/year` : "N/A", bg: "secondary.main" },
                                { label: "Peak Sun Hours", value: userDetail.psh_estimate ? `${userDetail.psh_estimate} hours/day` : "N/A", bg: "warning.main" },
                                { label: "Battery C Rating", value: val(userDetail.battery_c_rating), bg: "info.main" },
                            ].map(({ label, value, bg }) => (
                                <Grid item xs={12} sm={6} md={3} key={label}>
                                    <MetricCard label={label} value={value} bgcolor={bg} />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Location Map ────────────────────────────────────────────── */}
                <Card sx={cardSx}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <LocationOnIcon color="primary" /> Location
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ height: { xs: "55vw", md: 400 }, borderRadius: 3, overflow: "hidden", position: "relative" }}>
                            {loadError ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                    <Alert severity="error">Error loading map. Please check your API key.</Alert>
                                </Box>
                            ) : !isLoaded ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <GoogleMap
                                    mapContainerStyle={{ width: "100%", height: "100%" }}
                                    center={center} zoom={15}
                                    options={{ disableDefaultUI: false, zoomControl: true }}
                                >
                                    <Marker position={center} />
                                </GoogleMap>
                            )}
                        </Box>
                    </CardContent>
                </Card>

            </Box>
        </Box>
    );
};

export default UserInfo;

